import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useChartColors } from "./chartTheme";
import { useChartReveal } from "./motion";
import type { DashboardOverview } from "@/types/api";

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const formattedDate = label ? new Date(label).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 p-2.5 shadow-xl backdrop-blur-sm text-xs">
      {formattedDate && <p className="mb-1.5 font-semibold text-slate-700 dark:text-slate-200">{formattedDate}</p>}
      <div className="space-y-1">
        {payload.map((entry) => {
          const title = entry.dataKey === "count" ? "Total Leads" : "Converted";
          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-slate-600 dark:text-slate-400">{title}</span>
              </div>
              <span className="font-bold tabular-nums text-slate-900 dark:text-white">
                {entry.value?.toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LeadsChart({ data }: { data: DashboardOverview["leadsOverTime"] }) {
  const colors = useChartColors();
  const { ref, revealed, isAnimationActive } = useChartReveal();
  const safeData = Array.isArray(data) ? data : [];

  return (
    <div ref={ref} className="h-52 md:h-56">
      {revealed && (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={safeData} margin={{ top: 6, right: 8, left: -4, bottom: 0 }}>
            <defs>
              <linearGradient id="leadsTotalFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.primary} stopOpacity={0.22} />
                <stop offset="100%" stopColor={colors.primary} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="leadsConvertedFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.success} stopOpacity={0.3} />
                <stop offset="100%" stopColor={colors.success} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="opacity-40 dark:opacity-20" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: colors.mutedForeground }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
              }
            />
            <YAxis tick={{ fontSize: 11, fill: colors.mutedForeground }} tickLine={false} axisLine={false} width={36} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="count"
              stroke={colors.primary}
              strokeWidth={2}
              fill="url(#leadsTotalFill)"
              isAnimationActive={isAnimationActive}
              animationDuration={900}
              animationEasing="ease-out"
            />
            <Area
              type="monotone"
              dataKey="converted"
              stroke={colors.success}
              strokeWidth={2}
              fill="url(#leadsConvertedFill)"
              isAnimationActive={isAnimationActive}
              animationDuration={900}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
